import { useState } from 'react';
import { Box, Chip, Collapse, IconButton, Typography } from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { IOrderError } from '../../../types/api/response/ordererrorsresponse';

interface Props {
  errors: IOrderError[] | null;
}

const formatDate = (val?: string | null) => {
  if (!val) return '—';
  return new Date(val).toLocaleString();
};

// Spring's RestClientResponseException#getMessage() (the source of `message` for dependency
// HTTP failures) is formatted as `{status} {reason}: "{response body}"` — e.g.
// `400 Bad Request: "[{"ErrorCode":400,"Exception":"..."}]"`. That embeds the full response
// body inline, which is already shown separately and in full under "Dependency response" —
// so strip it down to the short status summary for display here.
const SPRING_HTTP_ERROR_MESSAGE = /^(\d{3}\s[^:]+):\s"/;

const formatMessage = (message: string) => {
  const match = message.match(SPRING_HTTP_ERROR_MESSAGE);
  return match ? match[1] : message;
};

const dependencyColor = (dependency: string | null): 'default' | 'warning' | 'info' | 'error' => {
  switch (dependency) {
    case 'CIN7':
      return 'warning';
    case 'LOCUS':
      return 'info';
    case 'PAYMENT_GATEWAY':
      return 'error';
    default:
      return 'default';
  }
};

const IdentityField = ({ label, value }: { label: string; value: string | null }) => (
  <Box>
    <Typography variant="caption" color="text.secondary" fontWeight={600}>
      {label}
    </Typography>
    <Typography variant="body2" mt={0.25} color={value ? 'text.primary' : 'text.disabled'}>
      {value ?? 'not assigned'}
    </Typography>
  </Box>
);

const DetailRow = ({ label, value }: { label: string; value?: string | null }) => {
  if (!value) return null;
  return (
    <Box mt={1}>
      <Typography variant="caption" color="text.secondary" fontWeight={600}>
        {label}
      </Typography>
      <Typography
        variant="body2"
        component="pre"
        sx={{
          whiteSpace: 'pre-wrap',
          wordBreak: 'break-word',
          fontFamily: 'monospace',
          fontSize: 11.5,
          backgroundColor: '#fafafa',
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 1,
          p: 1,
          mt: 0.5,
          maxHeight: 220,
          overflow: 'auto',
        }}
      >
        {value}
      </Typography>
    </Box>
  );
};

// Different error rows for the same order can have different identity fields populated
// (e.g. an early CIN7_SALE_CREATE failure has no ds_sale_id yet, a later row does) — scan
// all rows per field rather than trusting the newest row to have everything.
const firstNonNull = (errors: IOrderError[], field: keyof IOrderError): string | null => {
  const match = errors.find((error) => error[field] != null);
  return match ? (match[field] as string) : null;
};

export const OrderErrorsPanel = ({ errors }: Props) => {
  const [expandedId, setExpandedId] = useState<number | null>(null);

  if (errors === null) {
    return (
      <Typography variant="body2" color="error">
        Failed to load errors for this order.
      </Typography>
    );
  }

  if (errors.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        No errors recorded for this order.
      </Typography>
    );
  }

  return (
    <Box display="flex" flexDirection="column" gap={1}>
      <Box
        display="grid"
        gridTemplateColumns="1fr 1fr 1fr"
        gap={2}
        sx={{
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 1,
          p: 1.5,
          backgroundColor: '#fafafa',
        }}
      >
        <IdentityField label="ORD- Order Number" value={firstNonNull(errors, 'order_number')} />
        <IdentityField label="Cin7 Sale ID" value={firstNonNull(errors, 'ds_sale_id')} />
        <IdentityField
          label="SO- DS Order Number"
          value={firstNonNull(errors, 'ds_order_number')}
        />
      </Box>

      {errors.map((error) => {
        const isExpanded = expandedId === error.id;
        return (
          <Box
            key={error.id}
            sx={{
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 1,
              overflow: 'hidden',
            }}
          >
            <Box
              display="flex"
              alignItems="flex-start"
              gap={1}
              p={1}
              sx={{ cursor: 'pointer' }}
              onClick={() => setExpandedId(isExpanded ? null : error.id)}
            >
              <IconButton size="small">
                {isExpanded ? (
                  <KeyboardArrowUpIcon fontSize="small" />
                ) : (
                  <KeyboardArrowDownIcon fontSize="small" />
                )}
              </IconButton>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Box display="flex" alignItems="center" gap={1}>
                  <Typography variant="caption" color="text.secondary" sx={{ minWidth: 140 }}>
                    {formatDate(error.created_at)}
                  </Typography>
                  <Typography variant="body2" sx={{ flex: 1, minWidth: 0 }} noWrap>
                    {formatMessage(error.message)}
                  </Typography>
                </Box>
                <Box display="flex" gap={0.75} flexWrap="wrap" mt={0.5}>
                  <Chip label={error.stage} size="small" variant="outlined" />
                  {error.dependency && (
                    <Chip
                      label={error.dependency}
                      size="small"
                      color={dependencyColor(error.dependency)}
                    />
                  )}
                  {error.error_code && (
                    <Chip label={error.error_code} size="small" variant="outlined" />
                  )}
                  {error.dependency_status != null && (
                    <Chip
                      label={`HTTP ${error.dependency_status}`}
                      size="small"
                      variant="outlined"
                    />
                  )}
                </Box>
              </Box>
            </Box>
            <Collapse in={isExpanded}>
              <Box px={1.5} pb={1.5}>
                <DetailRow label="User phone" value={error.user_phone} />
                <DetailRow label="Dependency response" value={error.dependency_response} />
                <DetailRow label="Dependency request" value={error.dependency_request} />
                <DetailRow
                  label="Context"
                  value={error.context ? JSON.stringify(error.context, null, 2) : null}
                />
                <DetailRow label="Correlation ID" value={error.correlation_id} />
                <DetailRow label="Stack trace" value={error.stack_trace} />
              </Box>
            </Collapse>
          </Box>
        );
      })}
    </Box>
  );
};
